import { useMemo, useState } from 'react';
import {
  BUDGET,
  CATS,
  MISC_BUDGET,
  WORKS_BUDGET,
  NEWD,
  REHAB,
  UNITS,
  barColor,
  catName,
  componentList,
  dLong,
  dShort,
  fmt,
  gaugeDash,
  netOf,
  num,
  pct1,
  sevColors,
  todayLocalIso,
  type Engine,
  type Week,
} from '@piaip/shared';
import { sx, sxc } from '../sx';
import { useApp } from '../state';
import { schemeStatus } from '../derive';
import { ProgressCurve } from '../components/ProgressCurve';
import {
  P_H,
  P_TD,
  P_TD_R,
  P_TD_SM,
  P_TD_SM_R,
  P_TH,
  P_TH_R,
  P_TH_SM,
  P_TH_SM_R,
  P_TILE,
} from '../components/ui';

type ReportKey = 'exec' | 'week' | 'month' | 'component' | 'financial' | 'delay';

/** Display name for a photograph's component tag; '' (the site in general) yields ''. */
function compTagName(k: string): string {
  return componentList().find((c) => c.k === k)?.n ?? '';
}

const REPORTS: [ReportKey, string][] = [
  ['exec', 'Executive Summary'],
  ['week', 'Weekly Report'],
  ['month', 'Monthly Report'],
  ['component', 'Component Detail'],
  ['financial', 'Financial Statement'],
  ['delay', 'Delay & Issues'],
];

const TITLES: Record<ReportKey, string> = {
  exec: 'Executive Progress Summary',
  week: 'Weekly Progress Report',
  month: 'Monthly Progress Report',
  component: 'Component-wise Progress Statement',
  financial: 'Financial Statement',
  delay: 'Delay & Issues Report',
};

export function Reports() {
  const { engine, orgName } = useApp();
  const [report, setReport] = useState<ReportKey>('exec');
  const [period, setPeriod] = useState('');

  const isPeriodic = report === 'week' || report === 'month';
  const today = todayLocalIso();

  const periodOptions = useMemo(() => {
    if (report === 'month') {
      return engine.monthKeys().map((k) => ({
        value: k,
        label: new Date(`${k}-01T00:00:00`).toLocaleDateString('en-GB', {
          month: 'long',
          year: 'numeric',
        }),
      }));
    }
    return engine
      .appr()
      .slice()
      .reverse()
      .map((w) => ({ value: w.id, label: `Week ${w.no} — ${dShort(w.end)}` }));
  }, [engine, report]);

  const per = isPeriodic ? engine.periodWeeks(report, period) : null;
  const periodLabel = per ? per.label : `Position as on ${dLong(today)}`;

  return (
    <div>
      <div
        data-chrome="1"
        style={sx(
          'background:#fff;border-bottom:1px solid #dbe3e0;padding:12px 14px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;position:sticky;top:0;z-index:5',
        )}
      >
        {REPORTS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              setReport(k);
              setPeriod('');
            }}
            style={sxc(
              'padding:8px 13px;border-radius:99px;font-size:13px;font-weight:500;cursor:pointer;white-space:nowrap',
              `background:${report === k ? '#0f5c52' : '#fff'}`,
              `color:${report === k ? '#fff' : '#33403d'}`,
              `border:1px solid ${report === k ? '#0f5c52' : '#d3dcda'}`,
            )}
          >
            {label}
          </button>
        ))}
        <div style={sx('flex:1')} />
        {isPeriodic ? (
          <select
            aria-label="Reporting period"
            value={period || periodOptions[0]?.value || ''}
            onChange={(e) => setPeriod(e.target.value)}
            style={sx(
              'padding:8px 10px;border:1px solid #d3dcda;border-radius:4px;font-size:13px;background:#fff;min-width:190px',
            )}
          >
            {periodOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : null}
        <button
          type="button"
          onClick={() => window.print()}
          style={sx(
            'background:#0f5c52;color:#fff;border:0;padding:9px 16px;border-radius:4px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap',
          )}
        >
          Print / Save as PDF
        </button>
      </div>

      <doc-page margin="0.7in" size="a4">
        <div
          slot="header"
          style={sx(
            'display:flex;justify-content:space-between;align-items:flex-end;border-bottom:1.5px solid #0d3b35;padding-bottom:6px;font-size:9pt;color:#5a6b68;gap:12px',
          )}
        >
          <div style={sx('font-weight:600;color:#0d3b35')}>{orgName}</div>
          <div>Mitigation of Waterlogging in District Rahim Yar Khan (PIAIP)</div>
        </div>
        <div
          slot="footer"
          style={sx(
            'display:flex;justify-content:space-between;border-top:1px solid #dbe3e0;padding-top:6px;font-size:8.5pt;color:#7d8b88;gap:12px',
          )}
        >
          <div>{TITLES[report]}</div>
          <div>Generated {dLong(today)}</div>
        </div>

        <div style={sx("font-family:'IBM Plex Sans',sans-serif;color:#12211f")}>
          <div style={sx('text-align:center;margin-bottom:22px')}>
            <div style={sx('font-size:10pt;letter-spacing:.14em;text-transform:uppercase;color:#5a6b68')}>
              {orgName}
            </div>
            <div
              style={sx(
                "font-family:'IBM Plex Serif',serif;font-size:19pt;font-weight:700;line-height:1.25;margin:8px 0 4px",
              )}
            >
              {TITLES[report]}
            </div>
            <div style={sx('font-size:11pt;color:#33403d')}>
              Mitigation of Waterlogging in District Rahim Yar Khan
            </div>
            <div style={sx('font-size:9.5pt;color:#5a6b68;margin-top:4px')}>{periodLabel}</div>
          </div>

          {report === 'exec' ? <ExecSummary /> : null}
          {isPeriodic && per ? <PeriodReport per={per} /> : null}
          {report === 'component' ? <ComponentDetail /> : null}
          {report === 'financial' ? <Financial /> : null}
          {report === 'delay' ? <DelayIssues /> : null}
        </div>
      </doc-page>
    </div>
  );
}

/* ------------------------------------------------ shared report pieces */

function StatusBox() {
  const { engine } = useApp();
  const s = schemeStatus(engine);
  return (
    <div style={sxc('padding:14px 16px;margin-bottom:18px', `background:${s.bg}`, `border-left:4px solid ${s.color}`)}>
      <div style={sxc('font-size:12pt;font-weight:700;margin-bottom:4px', `color:${s.color}`)}>
        {s.head}
      </div>
      <div style={sx('font-size:10.5pt;line-height:1.55;color:#22312e')}>{s.text}</div>
    </div>
  );
}

function PrintTiles({ tiles, big = true }: { tiles: { label: string; value: string; sub: string; color: string }[]; big?: boolean }) {
  return (
    <div style={sx('display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px')}>
      {tiles.map((t) => (
        <div key={t.label} style={sx(P_TILE)}>
          <div
            style={sx(
              'font-size:8pt;letter-spacing:.07em;text-transform:uppercase;color:#5a6b68;font-weight:600;line-height:1.3;min-height:22px',
            )}
          >
            {t.label}
          </div>
          <div
            style={sxc(
              `font-family:'IBM Plex Mono',monospace;font-size:${big ? '17pt' : '16pt'};font-weight:500;margin:4px 0 2px`,
              `color:${t.color}`,
            )}
          >
            {t.value}
          </div>
          <div style={sx('font-size:8.5pt;color:#5a6b68;line-height:1.35')}>{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

function PrintBar({ pct, color, width = 38 }: { pct: number; color: string; width?: number }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:6px')}>
      <div style={sx('flex:1;height:8px;background:#eef2f1')}>
        <div
          style={{ height: '100%', width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: color }}
        />
      </div>
      <div style={{ width: `${width}px`, textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace" }}>
        {pct1(pct)}%
      </div>
    </div>
  );
}

function PrintDial({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={sx('flex:1;display:flex;flex-direction:column;align-items:center;gap:5px')}>
      <div style={sx('position:relative;width:100%')}>
        <svg viewBox="0 0 140 140" style={sx('width:100%;height:auto;display:block')}>
          <circle cx="70" cy="70" r="54" fill="none" stroke="#e7ecea" strokeWidth="16" />
          <circle
            cx="70"
            cy="70"
            r="54"
            fill="none"
            stroke={color}
            strokeWidth="16"
            strokeLinecap="butt"
            strokeDasharray={gaugeDash(value)}
            transform="rotate(-90 70 70)"
          />
        </svg>
        <div
          style={sx(
            "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:'IBM Plex Mono',monospace;font-size:11pt;color:#0d3b35",
          )}
        >
          {pct1(value)}%
        </div>
      </div>
      <div style={sx('font-size:8pt;color:#5a6b68')}>{label}</div>
    </div>
  );
}

/* ------------------------------------------------- executive summary */

function ExecSummary() {
  const { engine, snapshot } = useApp();
  const s = schemeStatus(engine);
  const { cats, spent, fin, t, gap } = s;

  const drains = engine.drainTotals();
  const structures = engine.structureTotals();

  const tiles = [
    { label: 'Work completed', value: `${pct1(cats.overall)}%`, sub: 'of the whole scheme', color: '#0d3b35' },
    {
      label: 'Money on works',
      value: `${pct1(fin.worksPct)}%`,
      sub: `Rs ${fmt(fin.works, 1)} Mn of ${fmt(WORKS_BUDGET, 0)} Mn construction`,
      color: '#0d3b35',
    },
    { label: 'Time used', value: `${pct1(t.pct)}%`, sub: `${t.remaining} days left`, color: '#0d3b35' },
    {
      label: 'Ahead / behind',
      value: `${gap >= 0 ? '+' : ''}${pct1(gap)}`,
      sub: 'points against time used',
      color: s.color,
    },
  ];

  const catRows = CATS.map((c) => ({
    name: c.n,
    weight: `${Math.round(c.w * 1000) / 10}%`,
    pct: cats[c.k],
    budget: fmt(c.budget, 1),
    spent: fmt(spent[c.k] || 0, 1),
  })).concat([
    {
      name: 'Land, environment, contingencies & tax',
      weight: '—',
      pct: engine.miscPct(),
      budget: fmt(MISC_BUDGET, 1),
      spent: fmt(spent.misc || 0, 1),
    },
  ]);

  const comp = cats.comp;
  const heatCells = componentList().map((c) => ({ name: c.n, pct: comp[c.k] }));
  const openIssues = (snapshot.issues ?? []).filter((i) => i.status !== 'Resolved');

  const narrative =
    'The scheme drains waterlogged land in District Rahim Yar Khan by cutting new drains, restoring existing ones, and installing the tubewells, pumps and power supply that move the water away. ' +
    `Of the ${fmt(drains.total)} feet of drain in the scheme, ${fmt(drains.done)} feet have been dug or restored. ` +
    `Of the ${structures.total} structures and machinery units to be built or installed, ${structures.done} are finished. ` +
    `Land acquisition, which is reported separately from construction, stands at ${pct1(num((snapshot.misc ?? {}).land))}%. ` +
    `Figures are compiled from ${engine.appr().length} weekly site returns approved by the PMU.`;

  return (
    <div>
      <StatusBox />
      <PrintTiles tiles={tiles} />

      <div
        style={sx('display:grid;grid-template-columns:1fr 1.35fr;gap:12px;margin-bottom:20px;break-inside:avoid')}
      >
        <div style={sx('border:1px solid #dbe3e0;padding:12px')}>
          <div
            style={sx(
              'font-size:8.5pt;text-transform:uppercase;letter-spacing:.06em;color:#5a6b68;font-weight:600;margin-bottom:10px',
            )}
          >
            Work, money and time
          </div>
          <div style={sx('display:flex;gap:8px')}>
            <PrintDial label="Work done" value={cats.overall} color="#0f5c52" />
            <PrintDial label="Money on works" value={fin.worksPct} color="#c9a227" />
            <PrintDial label="Time used" value={t.pct} color="#4b8ea8" />
          </div>
        </div>
        <div style={sx('border:1px solid #dbe3e0;padding:12px')}>
          <div
            style={sx(
              'font-size:8.5pt;text-transform:uppercase;letter-spacing:.06em;color:#5a6b68;font-weight:600;margin-bottom:8px',
            )}
          >
            Progress week by week
          </div>
          <ProgressCurve engine={engine} print />
        </div>
      </div>

      <div style={sx(P_H)}>Where the scheme stands</div>
      <div style={sx('font-size:10.5pt;line-height:1.6;margin-bottom:16px;color:#22312e')}>{narrative}</div>

      <div style={sx(P_H)}>Progress of the four heads of work</div>
      <table style={sx('width:100%;border-collapse:collapse;margin-bottom:18px;font-size:10pt')}>
        <thead>
          <tr style={sx('background:#f2f6f5')}>
            <th style={sx(P_TH)}>Head of work</th>
            <th style={sx(P_TH_R)}>Share</th>
            <th style={sx(P_TH)}>Work done</th>
            <th style={sx(P_TH_R)}>Cost (Rs Mn)</th>
            <th style={sx(P_TH_R)}>Spent (Rs Mn)</th>
          </tr>
        </thead>
        <tbody>
          {catRows.map((c) => (
            <tr key={c.name}>
              <td style={sx(P_TD)}>{c.name}</td>
              <td style={sxc(P_TD, 'text-align:right')}>{c.weight}</td>
              <td style={sx(P_TD)}>
                <div style={sx('display:flex;align-items:center;gap:8px')}>
                  <div style={sx('flex:1;height:9px;background:#e7ecea;border-radius:2px;overflow:hidden')}>
                    <div style={{ height: '100%', background: '#0f5c52', width: `${Math.min(100, c.pct)}%` }} />
                  </div>
                  <div style={sx("width:42px;text-align:right;font-family:'IBM Plex Mono',monospace")}>
                    {pct1(c.pct)}%
                  </div>
                </div>
              </td>
              <td style={sx(P_TD_R)}>{c.budget}</td>
              <td style={sx(P_TD_R)}>{c.spent}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={sx('break-inside:avoid;margin-bottom:20px')}>
        <div style={sx(P_H)}>How far each part of the work has come</div>
        <div style={sx('display:grid;grid-template-columns:1fr 1fr;gap:6px 18px')}>
          {heatCells.map((h) => (
            <div key={h.name} style={sx('display:flex;align-items:center;gap:8px')}>
              <div style={sx('flex:1;font-size:9pt;line-height:1.3')}>{h.name}</div>
              <div style={sx('width:86px;height:9px;background:#e7ecea;flex:none')}>
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(100, h.pct)}%`,
                    backgroundColor: barColor(h.pct),
                  }}
                />
              </div>
              <div
                style={sx(
                  "width:38px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:9pt;flex:none",
                )}
              >
                {pct1(h.pct)}%
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={sx(P_H)}>Matters requiring the competent authority&rsquo;s attention</div>
      {openIssues.length ? (
        <ol style={sx('margin:0 0 16px;padding-left:20px;font-size:10.5pt;line-height:1.6;color:#22312e')}>
          {openIssues.slice(0, 6).map((i) => (
            <li key={i.id} style={sx('margin-bottom:7px')}>
              <strong>{i.title || 'Untitled issue'}</strong> — {i.impact || 'No effect recorded.'}{' '}
              <em style={sx('color:#5a6b68')}>
                ({i.owner || 'Not assigned'}, {i.status || 'Open'})
              </em>
            </li>
          ))}
        </ol>
      ) : (
        <div style={sx('font-size:10.5pt;color:#5a6b68;margin-bottom:16px')}>
          No outstanding matters have been reported for this period.
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- weekly / monthly */

function PeriodReport({ per }: { per: ReturnType<Engine['periodWeeks']> }) {
  const { engine } = useApp();
  const upto = per.upto;
  const inPeriod: Week[] = per.list.filter(Boolean);

  const pCats = engine.catPcts(upto);
  const pComp = pCats.comp;
  const pSpent = engine.spentBy(upto);
  const priorOverall = per.first !== undefined && per.first > 0 ? engine.catPcts(per.first - 1).overall : 0;

  const sumW = (kind: 'drainsNew' | 'drainsRehab', key: string) =>
    inPeriod.reduce((a, x) => a + num((x[kind] || {})[key]), 0);
  const sumStage = (gk: string, i: number) =>
    inPeriod.reduce((a, x) => a + num(((x.units || {})[gk] || {})[i]), 0);

  const tiles = [
    { label: 'Work completed to date', value: `${pct1(pCats.overall)}%`, sub: 'of the whole scheme', color: '#0d3b35' },
    {
      label: 'Added this period',
      value: `${pct1(Math.max(0, pCats.overall - priorOverall))}%`,
      sub: 'increase in physical progress',
      color: '#0d3b35',
    },
    {
      label: 'Paid this period',
      value: `Rs ${fmt(
        inPeriod.reduce((a, x) => a + (x.expenses || []).reduce((b, e) => b + num(e.amt), 0), 0),
        1,
      )}`,
      sub: 'million',
      color: '#0d3b35',
    },
    {
      label: 'Spent to date',
      value: `Rs ${fmt(pSpent.total, 1)}`,
      sub: `million of ${fmt(BUDGET, 0)}`,
      color: '#0d3b35',
    },
  ];

  const narrative =
    inPeriod
      .map((x) => x.narrative)
      .filter(Boolean)
      .join('\n\n') || 'No narrative was recorded for this period.';

  const expRows = inPeriod.flatMap((x) => (x.expenses || []).map((e) => ({ ...e, weekId: x.id })));
  const matRows = inPeriod.flatMap((x) => (x.material || []).map((m) => ({ ...m, weekId: x.id })));
  const photos = inPeriod.flatMap((x) => x.photos || []);

  const footnote =
    inPeriod.length && inPeriod[0].approvedAt
      ? `This return was submitted by ${inPeriod[0].submittedBy || 'the Field Officer'} and approved by ${
          inPeriod[0].approvedBy || 'the PMU In-charge'
        } on ${dLong(inPeriod[0].approvedAt)}.`
      : 'Compiled from weekly site returns approved by the PMU.';

  return (
    <div>
      <PrintTiles tiles={tiles} />

      <div style={sx(P_H)}>Summary of the period</div>
      <div style={sx('font-size:10.5pt;line-height:1.6;margin-bottom:18px;color:#22312e;white-space:pre-wrap')}>
        {narrative}
      </div>

      <div style={sx(P_H)}>Work executed — excavation of drains</div>
      <table style={sx('width:100%;border-collapse:collapse;margin-bottom:18px;font-size:9.5pt')}>
        <thead>
          <tr style={sx('background:#f2f6f5')}>
            <th style={sx(P_TH_SM)}>Drain</th>
            <th style={sx(P_TH_SM_R)}>Total (ft)</th>
            <th style={sx(P_TH_SM_R)}>This period (ft)</th>
            <th style={sx(P_TH_SM_R)}>To date (ft)</th>
            <th style={sx(P_TH_SM_R)}>%</th>
          </tr>
        </thead>
        <tbody>
          {(
            [
              ['Construction of new drains', 'drainsNew', NEWD],
              ['Rehabilitation of existing drains', 'drainsRehab', REHAB],
            ] as const
          ).flatMap(([title, kind, list]) => [
            <tr key={title} style={sx('background:#f2f6f5')}>
              <td style={sxc(P_TD_SM, 'font-weight:700')}>{title}</td>
              <td style={sx(P_TD_SM)} />
              <td style={sx(P_TD_SM)} />
              <td style={sx(P_TD_SM)} />
              <td style={sx(P_TD_SM)} />
            </tr>,
            ...list.map((d) => {
              const cum = Math.min(d.ft, engine.cumDrain(kind, d.k, upto));
              const pv = (cum / d.ft) * 100;
              return (
                <tr key={`${kind}-${d.k}`}>
                  <td style={sx(P_TD_SM)}>{d.n}</td>
                  <td style={sx(P_TD_SM_R)}>{fmt(d.ft)}</td>
                  <td style={sx(P_TD_SM_R)}>{fmt(sumW(kind, d.k))}</td>
                  <td style={sx(P_TD_SM_R)}>{fmt(cum)}</td>
                  <td style={sxc(P_TD_SM, 'width:120px')}>
                    <PrintBar pct={pv} color={barColor(pv)} />
                  </td>
                </tr>
              );
            }),
          ])}
        </tbody>
      </table>

      <div style={sx(P_H)}>Work executed — structures, electrical and mechanical</div>
      <table style={sx('width:100%;border-collapse:collapse;margin-bottom:18px;font-size:9.5pt')}>
        <thead>
          <tr style={sx('background:#f2f6f5')}>
            <th style={sx(P_TH_SM)}>Component / stage</th>
            <th style={sx(P_TH_SM_R)}>In scope</th>
            <th style={sx(P_TH_SM_R)}>This period</th>
            <th style={sx(P_TH_SM_R)}>To date</th>
            <th style={sx(P_TH_SM_R)}>Component %</th>
          </tr>
        </thead>
        <tbody>
          {UNITS.flatMap((g) => [
            <tr key={g.k} style={sx('background:#f2f6f5')}>
              <td style={sxc(P_TD_SM, 'font-weight:700;padding-left:8px')}>{g.n}</td>
              <td style={sx(P_TD_SM_R)}>{g.total}</td>
              <td style={sx(P_TD_SM)} />
              <td style={sx(P_TD_SM)} />
              <td style={sxc(P_TD_SM, 'width:120px')}>
                <PrintBar pct={pComp[g.k]} color={barColor(pComp[g.k])} />
              </td>
            </tr>,
            ...g.stages.map((st, i) => (
              <tr key={`${g.k}-${i}`}>
                <td style={sxc(P_TD_SM, 'padding-left:22px')}>{st[0]}</td>
                <td style={sx(P_TD_SM)} />
                <td style={sx(P_TD_SM_R)}>{fmt(sumStage(g.k, i))}</td>
                <td style={sx(P_TD_SM_R)}>{fmt(Math.min(g.total, engine.cumStage(g.k, i, upto)))}</td>
                <td style={sx(P_TD_SM)} />
              </tr>
            )),
          ])}
        </tbody>
      </table>

      {expRows.length ? (
        <div>
          <div style={sx(P_H)}>Payments released during the period</div>
          <table style={sx('width:100%;border-collapse:collapse;margin-bottom:18px;font-size:9.5pt')}>
            <thead>
              <tr style={sx('background:#f2f6f5')}>
                <th style={sx(P_TH_SM)}>Date</th>
                <th style={sx(P_TH_SM)}>Head</th>
                <th style={sx(P_TH_SM)}>Particulars</th>
                <th style={sx(P_TH_SM)}>Paid to</th>
                <th style={sx(P_TH_SM_R)}>Rs Mn</th>
              </tr>
            </thead>
            <tbody>
              {expRows.map((e) => (
                <tr key={e.id}>
                  <td style={sx(P_TD_SM)}>{dShort(e.date)}</td>
                  <td style={sx(P_TD_SM)}>{catName(e.cat)}</td>
                  <td style={sx(P_TD_SM)}>{e.desc || '—'}</td>
                  <td style={sx(P_TD_SM)}>{e.vendor || '—'}</td>
                  <td style={sx(P_TD_SM_R)}>{fmt(e.amt, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {matRows.length ? (
        <div>
          <div style={sx(P_H)}>Material available at site</div>
          <table style={sx('width:100%;border-collapse:collapse;margin-bottom:18px;font-size:9.5pt')}>
            <thead>
              <tr style={sx('background:#f2f6f5')}>
                <th style={sx(P_TH_SM)}>Site</th>
                {['Cement', 'Sand', 'Crush', 'Steel', 'Shuttering'].map((h) => (
                  <th key={h} style={sxc(P_TH_SM, 'text-align:center')}>
                    {h}
                  </th>
                ))}
                <th style={sx(P_TH_SM)}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {matRows.map((m) => (
                <tr key={m.id}>
                  <td style={sx(P_TD_SM)}>{m.site || '—'}</td>
                  {(['cement', 'sand', 'crush', 'steel', 'shuttering'] as const).map((k) => (
                    <td key={k} style={sxc(P_TD_SM, 'text-align:center')}>
                      {m[k] || 'No'}
                    </td>
                  ))}
                  <td style={sx(P_TD_SM)}>{m.rem}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {photos.length ? (
        <div style={sx('break-inside:avoid')}>
          <div style={sx(P_H)}>Photographs from site</div>
          <div style={sx('display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:18px')}>
            {photos.map((p) => (
              <figure key={p.id} style={sx('margin:0;break-inside:avoid')}>
                <img
                  src={p.src}
                  alt={p.cap || 'site photograph'}
                  style={sx('width:100%;height:130px;object-fit:cover;border:1px solid #dbe3e0')}
                />
                <figcaption style={sx('font-size:8.5pt;color:#5a6b68;margin-top:4px')}>
                  {p.cap || 'Site photograph'}
                  {compTagName(p.comp) ? ` — ${compTagName(p.comp)}` : ''}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      ) : null}

      <div style={sx('font-size:9pt;color:#5a6b68;border-top:1px solid #dbe3e0;padding-top:8px;margin-top:26px')}>
        {footnote}
      </div>
    </div>
  );
}

/* ------------------------------------------------- component detail */

function ComponentDetail() {
  const { engine, snapshot } = useApp();
  const comp = engine.compPcts();
  const today = todayLocalIso();

  /** Approved photographs by component key ('' = the site in general), in week order. */
  const photoRecord = new Map<string, { id: string; src: string; cap: string; week: Week }[]>();
  engine.appr().forEach((w) => {
    (w.photos || []).forEach((p) => {
      const list = photoRecord.get(p.comp) ?? [];
      list.push({ id: p.id, src: p.src, cap: p.cap, week: w });
      photoRecord.set(p.comp, list);
    });
  });

  const photoStrip = (key: string) => {
    const shots = photoRecord.get(key);
    if (!shots?.length) return null;
    return (
      <div style={sx('margin-top:10px;break-inside:avoid')}>
        <div style={sx('font-size:9pt;font-weight:700;color:#33403d;margin-bottom:6px')}>
          Photographic record
        </div>
        <div style={sx('display:grid;grid-template-columns:repeat(4,1fr);gap:8px')}>
          {shots.map((sh) => (
            <figure key={sh.id} style={sx('margin:0;break-inside:avoid')}>
              <img
                src={sh.src}
                alt={sh.cap || 'site photograph'}
                style={sx('width:100%;height:96px;object-fit:cover;border:1px solid #dbe3e0')}
              />
              <figcaption style={sx('font-size:8pt;color:#5a6b68;margin-top:3px')}>
                Week {sh.week.no}
                {sh.week.end ? `, ${dShort(sh.week.end)}` : ''}
                {sh.cap ? ` — ${sh.cap}` : ''}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    );
  };

  const sections = [
    {
      key: 'newDrains',
      title: 'Construction of new drains',
      pct: comp.newDrains,
      cols: ['Drain', 'Total length (ft)', 'Completed (ft)'],
      rows: NEWD.map((d) => {
        const c = Math.min(d.ft, engine.cumDrain('drainsNew', d.k));
        return { name: d.n, a: fmt(d.ft), b: fmt(c), pct: (c / d.ft) * 100, pkg: engine.pkgOf('newDrains') };
      }),
    },
    {
      key: 'rehabDrains',
      title: 'Rehabilitation of existing drains',
      pct: comp.rehabDrains,
      cols: ['Drain', 'Total length (ft)', 'Completed (ft)'],
      rows: REHAB.map((d) => {
        const c = Math.min(d.ft, engine.cumDrain('drainsRehab', d.k));
        return {
          name: d.n,
          a: fmt(d.ft),
          b: fmt(c),
          pct: (c / d.ft) * 100,
          pkg: engine.pkgOf('rehabDrains'),
        };
      }),
    },
    ...UNITS.map((g) => ({
      key: g.k,
      title: `${g.n} (${g.total} Nos.)`,
      pct: comp[g.k],
      cols: ['Stage', 'Weight', 'Units done'],
      rows: g.stages.map((st, i) => {
        const c = Math.min(g.total, engine.cumStage(g.k, i));
        return {
          name: st[0],
          a: `${st[1]}%`,
          b: `${c} / ${g.total}`,
          pct: (c / g.total) * 100,
          pkg: engine.pkgOf(g.k),
        };
      }),
    })),
  ];

  const pkgRows = (snapshot.packages ?? []).map((p) => {
    const ks = Object.keys(snapshot.assign ?? {}).filter((k) => snapshot.assign[k] === p.id);
    const names = ks.map((k) => componentList().find((c) => c.k === k)?.n).filter(Boolean);
    return {
      id: p.id,
      name: p.name || 'Unnamed package',
      contractor: p.contractor || '—',
      components: names.join('; ') || 'No components assigned',
      pct: engine.pkgPct(p.id, comp),
    };
  });

  return (
    <div>
      <div style={sx('font-size:10.5pt;line-height:1.6;margin-bottom:16px;color:#22312e')}>
        This statement sets out every channel, structure and machinery item in the scheme, the
        quantity of work in each, and how much of it has been executed to {dLong(today)}.
      </div>

      {sections.map((s) => (
        <div key={s.key} style={sx('margin-bottom:20px;break-inside:avoid')}>
          <div
            style={sx(
              'display:flex;justify-content:space-between;align-items:baseline;border-bottom:1.5px solid #0d3b35;padding-bottom:5px;margin-bottom:9px;gap:12px',
            )}
          >
            <div style={sx("font-family:'IBM Plex Serif',serif;font-size:12.5pt;font-weight:700")}>
              {s.title}
            </div>
            <div
              style={sx("font-family:'IBM Plex Mono',monospace;font-size:11pt;color:#0f5c52;white-space:nowrap")}
            >
              {pct1(s.pct)}% complete
            </div>
          </div>
          <table style={sx('width:100%;border-collapse:collapse;font-size:9.5pt')}>
            <thead>
              <tr style={sx('background:#f2f6f5')}>
                <th style={sx(P_TH_SM)}>{s.cols[0]}</th>
                <th style={sx(P_TH_SM_R)}>{s.cols[1]}</th>
                <th style={sx(P_TH_SM_R)}>{s.cols[2]}</th>
                <th style={sxc(P_TH_SM, 'width:34%')}>Status</th>
                <th style={sx(P_TH_SM)}>Package</th>
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r) => (
                <tr key={r.name}>
                  <td style={sx(P_TD_SM)}>{r.name}</td>
                  <td style={sx(P_TD_SM_R)}>{r.a}</td>
                  <td style={sx(P_TD_SM_R)}>{r.b}</td>
                  <td style={sx(P_TD_SM)}>
                    <div style={sx('display:flex;align-items:center;gap:7px')}>
                      <div style={sx('flex:1;height:8px;background:#e7ecea;border-radius:2px;overflow:hidden')}>
                        <div
                          style={{
                            height: '100%',
                            background: barColor(r.pct),
                            width: `${Math.min(100, r.pct)}%`,
                          }}
                        />
                      </div>
                      <div style={sx("width:38px;text-align:right;font-family:'IBM Plex Mono',monospace")}>
                        {pct1(r.pct)}%
                      </div>
                    </div>
                  </td>
                  <td style={sxc(P_TD_SM, 'font-size:9pt;color:#5a6b68')}>{r.pkg}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {photoStrip(s.key)}
        </div>
      ))}

      {photoRecord.get('')?.length ? (
        <div style={sx('margin-bottom:20px;break-inside:avoid')}>
          <div
            style={sx(
              "font-family:'IBM Plex Serif',serif;font-size:12.5pt;font-weight:700;border-bottom:1.5px solid #0d3b35;padding-bottom:5px;margin-bottom:9px",
            )}
          >
            The site in general
          </div>
          {photoStrip('')}
        </div>
      ) : null}

      <div style={sx('break-before:page')}>
        <div style={sx(P_H)}>Progress by contract package</div>
        <table style={sx('width:100%;border-collapse:collapse;font-size:10pt;margin-bottom:16px')}>
          <thead>
            <tr style={sx('background:#f2f6f5')}>
              <th style={sx(P_TH)}>Package</th>
              <th style={sx(P_TH)}>Contractor</th>
              <th style={sx(P_TH)}>Components</th>
              <th style={sx(P_TH_R)}>Progress</th>
            </tr>
          </thead>
          <tbody>
            {pkgRows.length ? (
              pkgRows.map((p) => (
                <tr key={p.id}>
                  <td style={sx(P_TD)}>{p.name}</td>
                  <td style={sx(P_TD)}>{p.contractor}</td>
                  <td style={sxc(P_TD, 'font-size:9pt;color:#33403d')}>{p.components}</td>
                  <td style={sx(P_TD_R)}>{pct1(p.pct)}%</td>
                </tr>
              ))
            ) : (
              <tr>
                <td style={sx(P_TD)} colSpan={4}>
                  No contract package has been set up.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- financial */

function Financial() {
  const { engine } = useApp();
  const s = schemeStatus(engine);
  const { cats, spent, fin } = s;

  const tiles = [
    { label: 'Sanctioned cost', value: fmt(BUDGET, 0), sub: 'Rs million', color: '#0d3b35' },
    { label: 'Expenditure to date', value: fmt(spent.total, 1), sub: 'Rs million', color: '#0d3b35' },
    { label: 'Balance available', value: fmt(BUDGET - spent.total, 1), sub: 'Rs million', color: '#0d3b35' },
    {
      label: 'Works paid vs work done',
      value: `${pct1(fin.worksPct)} / ${pct1(cats.overall)}`,
      sub: 'construction money vs work finished',
      color: s.color,
    },
  ];

  const rows = CATS.map((c) => ({
    name: c.n,
    budget: fmt(c.budget, 1),
    spent: fmt(spent[c.k] || 0, 1),
    bal: fmt(c.budget - (spent[c.k] || 0), 1),
    util: `${pct1(((spent[c.k] || 0) / c.budget) * 100)}%`,
    phys: `${pct1(cats[c.k])}%`,
    bold: false,
  }))
    .concat([
      {
        name: 'Construction work',
        budget: fmt(WORKS_BUDGET, 1),
        spent: fmt(fin.works, 1),
        bal: fmt(WORKS_BUDGET - fin.works, 1),
        util: `${pct1(fin.worksPct)}%`,
        phys: `${pct1(cats.overall)}%`,
        bold: true,
      },
      {
        name: 'Land, environment, contingencies & tax',
        budget: fmt(MISC_BUDGET, 1),
        spent: fmt(spent.misc || 0, 1),
        bal: fmt(MISC_BUDGET - (spent.misc || 0), 1),
        util: `${pct1(((spent.misc || 0) / MISC_BUDGET) * 100)}%`,
        phys: `${pct1(engine.miscPct())}%`,
        bold: false,
      },
    ])
    .concat([
      {
        name: 'Total',
        budget: fmt(BUDGET, 1),
        spent: fmt(spent.total, 1),
        bal: fmt(BUDGET - spent.total, 1),
        util: `${pct1(fin.overallPct)}%`,
        phys: '—',
        bold: true,
      },
    ]);

  const narrative =
    `Payments on construction work stand at ${pct1(fin.worksPct)}% of the construction budget against physical work of ${pct1(
      cats.overall,
    )}%. ` +
    `Including land, environment, contingencies and tax, which produce no physical work and are reported separately, ` +
    `${pct1(fin.overallPct)}% of the sanctioned cost has been spent. ` +
    (fin.worksPct > cats.overall + 5
      ? 'Construction payments have run ahead of work done; this is normal where mobilisation advances or procurement of plant fall early in the programme, but it should be watched.'
      : 'Spending is broadly in step with the work executed on the ground.');

  const em = engine.expByMonth();
  const emMax = Math.max(1, ...em.map((x) => x.v));

  let prun = 0;
  const payRows = engine.payList().map((p) => {
    prun += netOf(p);
    return {
      id: p.id,
      date: dShort(p.date),
      ipc: p.ipc || '—',
      contractor: p.contractor || '—',
      cat: catName(p.cat),
      gross: fmt(p.gross, 3),
      ded: fmt(num(p.retention) + num(p.tax), 3),
      net: fmt(netOf(p), 3),
      cum: fmt(prun, 3),
    };
  });

  let run = 0;
  const expRows: { id: string; date: string; cat: string; desc: string; vendor: string; amt: string; cum: string }[] =
    [];
  engine.appr().forEach((w) =>
    (w.expenses || []).forEach((e) => {
      run += num(e.amt);
      expRows.push({
        id: e.id,
        date: dShort(e.date),
        cat: catName(e.cat),
        desc: e.desc || '—',
        vendor: e.vendor || '—',
        amt: fmt(e.amt, 1),
        cum: fmt(run, 1),
      });
    }),
  );

  return (
    <div>
      <PrintTiles tiles={tiles} big={false} />

      <div style={sx(P_H)}>Sanctioned cost and expenditure by head</div>
      <table style={sx('width:100%;border-collapse:collapse;font-size:10pt;margin-bottom:20px')}>
        <thead>
          <tr style={sx('background:#f2f6f5')}>
            <th style={sx(P_TH)}>Head of account</th>
            <th style={sx(P_TH_R)}>Sanctioned</th>
            <th style={sx(P_TH_R)}>Expenditure</th>
            <th style={sx(P_TH_R)}>Balance</th>
            <th style={sx(P_TH_R)}>Utilised</th>
            <th style={sx(P_TH_R)}>Work done</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr
              key={f.name}
              style={sxc(f.bold ? 'background:#f2f6f5;font-weight:700' : 'background:transparent;font-weight:400')}
            >
              <td style={sx(P_TD)}>{f.name}</td>
              <td style={sx(P_TD_R)}>{f.budget}</td>
              <td style={sx(P_TD_R)}>{f.spent}</td>
              <td style={sx(P_TD_R)}>{f.bal}</td>
              <td style={sx(P_TD_R)}>{f.util}</td>
              <td style={sx(P_TD_R)}>{f.phys}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={sx('font-size:9pt;color:#5a6b68;margin:-12px 0 18px')}>All figures in Rs million.</div>

      <div style={sx('font-size:10.5pt;line-height:1.6;margin-bottom:20px;color:#22312e')}>{narrative}</div>

      {em.length ? (
        <div style={sx('break-inside:avoid;margin-bottom:20px')}>
          <div style={sx(P_H)}>Money released, month by month</div>
          <div style={sx('border:1px solid #dbe3e0;padding:12px;display:flex;gap:6px;align-items:flex-end;height:150px')}>
            {em.map((x) => (
              <div
                key={x.k}
                style={sx('flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;height:100%')}
              >
                <div style={sx("font-size:7.5pt;color:#5a6b68;font-family:'IBM Plex Mono',monospace")}>
                  {fmt(x.v, 1)}
                </div>
                <div style={sx('flex:1;width:100%;display:flex;align-items:flex-end')}>
                  <div
                    style={{
                      width: '100%',
                      height: `${Math.max(2, (x.v / emMax) * 100)}%`,
                      background: '#0f5c52',
                    }}
                  />
                </div>
                <div style={sx('font-size:7.5pt;color:#5a6b68;text-align:center;line-height:1.2')}>
                  {new Date(`${x.k}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' })} &rsquo;
                  {x.k.slice(2, 4)}
                </div>
              </div>
            ))}
          </div>
          <div style={sx('font-size:8.5pt;color:#5a6b68;margin-top:5px')}>
            Net payments to contractors and other charges, Rs million.
          </div>
        </div>
      ) : null}

      {payRows.length ? (
        <div>
          <div style={sx(P_H)}>Payments to contractors — running bills</div>
          <table style={sx('width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:20px')}>
            <thead>
              <tr style={sx('background:#f2f6f5')}>
                <th style={sx(P_TH_SM)}>Date</th>
                <th style={sx(P_TH_SM)}>IPC / bill</th>
                <th style={sx(P_TH_SM)}>Contractor</th>
                <th style={sx(P_TH_SM)}>Head</th>
                <th style={sx(P_TH_SM_R)}>Gross</th>
                <th style={sx(P_TH_SM_R)}>Deductions</th>
                <th style={sx(P_TH_SM_R)}>Net paid</th>
                <th style={sx(P_TH_SM_R)}>Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {payRows.map((p) => (
                <tr key={p.id}>
                  <td style={sx(P_TD_SM)}>{p.date}</td>
                  <td style={sx(P_TD_SM)}>{p.ipc}</td>
                  <td style={sx(P_TD_SM)}>{p.contractor}</td>
                  <td style={sx(P_TD_SM)}>{p.cat}</td>
                  <td style={sx(P_TD_SM_R)}>{p.gross}</td>
                  <td style={sx(P_TD_SM_R)}>{p.ded}</td>
                  <td style={sx(P_TD_SM_R)}>{p.net}</td>
                  <td style={sx(P_TD_SM_R)}>{p.cum}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div style={sx(P_H)}>Register of payments</div>
      <table style={sx('width:100%;border-collapse:collapse;font-size:9.5pt')}>
        <thead>
          <tr style={sx('background:#f2f6f5')}>
            <th style={sx(P_TH_SM)}>Date</th>
            <th style={sx(P_TH_SM)}>Head</th>
            <th style={sx(P_TH_SM)}>Particulars</th>
            <th style={sx(P_TH_SM)}>Paid to</th>
            <th style={sx(P_TH_SM_R)}>Rs Mn</th>
            <th style={sx(P_TH_SM_R)}>Cumulative</th>
          </tr>
        </thead>
        <tbody>
          {expRows.length ? (
            expRows.map((e) => (
              <tr key={e.id}>
                <td style={sx(P_TD_SM)}>{e.date}</td>
                <td style={sx(P_TD_SM)}>{e.cat}</td>
                <td style={sx(P_TD_SM)}>{e.desc}</td>
                <td style={sx(P_TD_SM)}>{e.vendor}</td>
                <td style={sx(P_TD_SM_R)}>{e.amt}</td>
                <td style={sx(P_TD_SM_R)}>{e.cum}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td style={sx(P_TD_SM)}>—</td>
              <td style={sx(P_TD_SM)}>—</td>
              <td style={sx(P_TD_SM)}>No payments recorded to date</td>
              <td style={sx(P_TD_SM)}>—</td>
              <td style={sx(P_TD_SM_R)}>0.0</td>
              <td style={sx(P_TD_SM_R)}>0.0</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------- delay & issues */

function DelayIssues() {
  const { engine, snapshot } = useApp();
  const t = engine.timeInfo();
  const comp = engine.compPcts();

  const delayRows = componentList()
    .map((c) => {
      const p = comp[c.k];
      const iss = (snapshot.issues ?? []).filter(
        (i) => i.component === c.n && i.status !== 'Resolved',
      );
      return {
        key: c.k,
        name: c.n,
        pkg: engine.pkgOf(c.k),
        pct: `${pct1(p)}%`,
        gap: `${pct1(t.pct - p)} pts`,
        cause: iss.length ? iss.map((i) => i.title || 'Untitled issue').join('; ') : 'No specific cause recorded',
        raw: p,
      };
    })
    .filter((r) => r.raw < t.pct);

  return (
    <div>
      <StatusBox />

      <div style={sx(P_H)}>Components lagging behind the elapsed time</div>
      <div style={sx('font-size:10pt;color:#5a6b68;margin-bottom:8px')}>
        Time elapsed on the scheme is {pct1(t.pct)}%. Any component below that figure is shown here.
      </div>
      <table style={sx('width:100%;border-collapse:collapse;font-size:10pt;margin-bottom:20px')}>
        <thead>
          <tr style={sx('background:#f2f6f5')}>
            <th style={sx(P_TH)}>Component</th>
            <th style={sx(P_TH)}>Package</th>
            <th style={sx(P_TH_R)}>Done</th>
            <th style={sx(P_TH_R)}>Shortfall</th>
            <th style={sx(P_TH)}>Reported cause</th>
          </tr>
        </thead>
        <tbody>
          {delayRows.length ? (
            delayRows.map((d) => (
              <tr key={d.key}>
                <td style={sx(P_TD)}>{d.name}</td>
                <td style={sxc(P_TD, 'font-size:9pt;color:#5a6b68')}>{d.pkg}</td>
                <td style={sx(P_TD_R)}>{d.pct}</td>
                <td style={sxc(P_TD_R, 'color:#a3372c')}>{d.gap}</td>
                <td style={sxc(P_TD, 'font-size:9.5pt')}>{d.cause}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td style={sx(P_TD)}>No component is behind the elapsed time</td>
              <td style={sx(P_TD)}>—</td>
              <td style={sx(P_TD_R)}>—</td>
              <td style={sx(P_TD_R)}>—</td>
              <td style={sx(P_TD)}>—</td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={sx(P_H)}>Issues register</div>
      {(snapshot.issues ?? []).length ? (
        (snapshot.issues ?? []).map((i) => {
          const c = sevColors(i.severity);
          return (
            <div
              key={i.id}
              style={sxc(
                'border:1px solid #dbe3e0;padding:11px 14px;margin-bottom:10px;break-inside:avoid',
                `border-left:4px solid ${c.fg}`,
              )}
            >
              <div
                style={sx('display:flex;justify-content:space-between;gap:12px;align-items:baseline;margin-bottom:5px')}
              >
                <div style={sx('font-size:11pt;font-weight:700')}>{i.title || 'Untitled issue'}</div>
                <div
                  style={sxc(
                    'font-size:8.5pt;text-transform:uppercase;letter-spacing:.06em;font-weight:700;white-space:nowrap',
                    `color:${c.fg}`,
                  )}
                >
                  {i.severity || 'Major'} · {i.status || 'Open'}
                </div>
              </div>
              <div style={sx('font-size:10pt;line-height:1.5;color:#22312e;margin-bottom:5px')}>
                {i.impact || 'No effect recorded.'}
              </div>
              <div style={sx('font-size:9.5pt;color:#5a6b68')}>
                Affects {i.component || 'Whole scheme'} · Responsible: {i.owner || 'Not assigned'} ·
                Target: {dShort(i.target)}
              </div>
              <div style={sx('font-size:9.5pt;color:#22312e;margin-top:4px')}>
                <strong>Action:</strong> {i.action || 'Not recorded'}
              </div>
            </div>
          );
        })
      ) : (
        <div style={sx('font-size:10pt;color:#5a6b68')}>No issue has been recorded.</div>
      )}
    </div>
  );
}
